import React, { Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

// ── Always-rendered / infrastructure (static) ───────────────────────────────
import { Toaster } from "./components/ui/toaster";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AlertProvider } from "./contexts/AlertContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import ScrollToTop from "./components/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute";
import EmployeePortalLayout from "./components/EmployeePortalLayout";
// These three are unconditionally rendered on every page — keep static so they
// are available before any route is resolved.
import LicenseLockout from "./components/LicenseLockout";
import LeadCaptureSystem from "./components/LeadCapture";
import ConversationalChat from "./components/ConversationalChat";
import { toast } from "./hooks/use-toast";
import { useAnalyticsInit, usePageTracking, useOutboundLinkTracking, usePlatformActivityPageView } from "./hooks/useAnalytics";
import { lazyWithRetry } from "./utils/lazyWithRetry";

// ── Lazy-loaded route components ────────────────────────────────────────────
// Each module is fetched only when its route is first visited, keeping the
// initial JS bundle lean and TTI fast.
const HomePage = lazyWithRetry(() => import("./components/HomePage"));
const SignInPage = lazyWithRetry(() => import("./components/SignInPage"));
const SSOCallback = lazyWithRetry(() => import("./components/SSOCallback"));
const AdminDashboard = lazyWithRetry(() => import("./components/AdminDashboard"));
const SuperAdminDashboard = lazyWithRetry(() => import("./components/SuperAdminDashboard"));
const SuperAdminLogin = lazyWithRetry(() => import("./components/SuperAdminLogin"));
const AdvancedMonitoringSetup = lazyWithRetry(() => import("./components/AdvancedMonitoringSetup"));
const SetupLanding = lazyWithRetry(() => import("./components/SetupLanding"));
const PolicyManagementDashboard = lazyWithRetry(() => import("./components/PolicyManagementDashboard"));

// Public Pages
const PricingPage = lazyWithRetry(() => import("./components/PricingPage"));
const FeaturesPage = lazyWithRetry(() => import("./components/FeaturesPage"));
const SolutionsPage = lazyWithRetry(() => import("./components/SolutionsPage"));
const ResourcesPage = lazyWithRetry(() => import("./components/ResourcesPage"));
const PartnerPage = lazyWithRetry(() => import("./components/PartnerPage"));
const ContactPage = lazyWithRetry(() => import("./components/ContactPage"));
const ScreenshotMonitoringPage = lazyWithRetry(() => import("./components/ScreenshotMonitoringPage"));
const ProductivityAnalyticsPage = lazyWithRetry(() => import("./components/ProductivityAnalyticsPage"));
const ComplianceSuitePage = lazyWithRetry(() => import("./components/ComplianceSuitePage"));
const InsiderRiskPage = lazyWithRetry(() => import("./components/InsiderRiskPage"));
const PrivacyPolicyPage = lazyWithRetry(() => import("./components/PrivacyPolicyPage"));
const SecurityCompliancePage = lazyWithRetry(() => import("./components/SecurityCompliancePage"));
const CompareCompetitorsPage = lazyWithRetry(() => import("./components/CompareCompetitorsPage"));
const ToolsPage = lazyWithRetry(() => import("./components/ToolsPage"));
const DeploymentGuidePage = lazyWithRetry(() => import("./components/DeploymentGuidePage"));

// Educational Resources Pages
const EmployeeMonitoringPrivacyPage = lazyWithRetry(() => import("./components/resources/EmployeeMonitoringPrivacyPage"));
const HIPAAMonitoringPage = lazyWithRetry(() => import("./components/resources/HIPAAMonitoringPage"));
const WorkforceIntelligencePage = lazyWithRetry(() => import("./components/resources/WorkforceIntelligencePage"));
const GDPRMonitoringPage = lazyWithRetry(() => import("./components/resources/GDPRMonitoringPage"));
const SOC2MonitoringPage = lazyWithRetry(() => import("./components/resources/SOC2MonitoringPage"));
const AIMonitoringPrivacyPage = lazyWithRetry(() => import("./components/resources/AIMonitoringPrivacyPage"));

// Compliance & Privacy Dashboards
const BillingDashboard = lazyWithRetry(() => import("./components/BillingDashboard"));
const PIMSRegistryDashboard = lazyWithRetry(() => import("./components/PIMSRegistryDashboard"));
const DPIADashboard = lazyWithRetry(() => import("./components/DPIADashboard"));
const ConsentLifecycleDashboard = lazyWithRetry(() => import("./components/ConsentLifecycleDashboard"));
const PrivacyTrainingDashboard = lazyWithRetry(() => import("./components/PrivacyTrainingDashboard"));
const DataCatalogDashboard = lazyWithRetry(() => import("./components/DataCatalogDashboard"));
const RetentionOrchestratorDashboard = lazyWithRetry(() => import("./components/RetentionOrchestratorDashboard"));
const CertificationTrackerDashboard = lazyWithRetry(() => import("./components/CertificationTrackerDashboard"));
const CAPADashboard = lazyWithRetry(() => import("./components/CAPADashboard"));
const AuditorPortalDashboard = lazyWithRetry(() => import("./components/AuditorPortalDashboard"));
const AIComplianceAdvisorDashboard = lazyWithRetry(() => import("./components/AIComplianceAdvisorDashboard"));
const ScreenIntelligenceDashboard = lazyWithRetry(() => import("./components/ScreenIntelligenceDashboard"));
const ROIEstimatorDashboard = lazyWithRetry(() => import("./components/ROIEstimatorDashboard"));
const GovernanceLoopDashboard = lazyWithRetry(() => import("./components/GovernanceLoopDashboard"));
const AdvancedAnalyticsDashboard = lazyWithRetry(() => import("./components/AdvancedAnalyticsDashboard"));
const TenantManagementDashboard = lazyWithRetry(() => import("./components/TenantManagementDashboard"));
const PaymentConfigDashboard = lazyWithRetry(() => import("./components/PaymentConfigDashboard"));

// Profile & Auth
const PasswordChange = lazyWithRetry(() => import("./components/PasswordChange"));
const ViewProfile = lazyWithRetry(() => import("./components/ViewProfile"));
const EditProfile = lazyWithRetry(() => import("./components/EditProfile"));
const AccountSettings = lazyWithRetry(() => import("./components/AccountSettings"));
const PaymentPage = lazyWithRetry(() => import("./components/PaymentPage"));
const TabHighlightDemo = lazyWithRetry(() => import("./components/TabHighlightDemo"));

// Security & AI Dashboards
const ApplicationMonitoringDashboardNew = lazyWithRetry(() => import("./components/ApplicationMonitoringDashboardNew"));
const ForensicsApprovalsDashboard = lazyWithRetry(() => import("./components/ForensicsApprovalsDashboard"));
const TeamProductivityConfigPage = lazyWithRetry(() => import("./components/TeamProductivityConfigPage"));
const UEBADashboard = lazyWithRetry(() => import("./components/UEBADashboard"));
const SOARDashboard = lazyWithRetry(() => import("./components/SOARDashboard"));
const SentimentAnalysisDashboard = lazyWithRetry(() => import("./components/SentimentAnalysisDashboard"));
const EmailDLPDashboard = lazyWithRetry(() => import("./components/EmailDLPDashboard"));
const ExfiltrationDetectionDashboard = lazyWithRetry(() => import("./components/ExfiltrationDetectionDashboard"));
const GitHubActivityDashboard = lazyWithRetry(() => import("./components/GitHubActivityDashboard"));
const SecurityPolicyDashboard = lazyWithRetry(() => import("./components/SecurityPolicyDashboard"));
const CloudDocumentsDashboard = lazyWithRetry(() => import("./components/CloudDocumentsDashboard"));
const EmailAnalyticsDashboard = lazyWithRetry(() => import("./components/EmailAnalyticsDashboard"));
const AutomationRulesBuilder = lazyWithRetry(() => import("./components/AutomationRulesBuilder"));
const TicketingIntegrations = lazyWithRetry(() => import("./components/TicketingIntegrations"));
const AlertsDashboard = lazyWithRetry(() => import("./components/AlertsDashboard"));

// M365 Integration Dashboards
const SharePointDashboard = lazyWithRetry(() => import("./components/SharePointDashboard"));
const YammerDashboard = lazyWithRetry(() => import("./components/YammerDashboard"));
const M365AuditDashboard = lazyWithRetry(() => import("./components/M365AuditDashboard"));

// Attendance
const EmployeeLeavePortal = lazyWithRetry(() => import("./components/attendance/EmployeeLeavePortal"));
const LeaveCalendar = lazyWithRetry(() => import("./components/attendance/LeaveCalendar"));
const MobileClockIn = lazyWithRetry(() => import("./components/attendance/MobileClockIn"));
const AttendanceKiosk = lazyWithRetry(() => import("./components/attendance/AttendanceKiosk"));

// Employee Portal
const EmployeePortalDashboard = lazyWithRetry(() => import("./components/EmployeePortalDashboard"));
const DeviceConsentPage = lazyWithRetry(() => import("./components/DeviceConsentPage"));
// Named exports require .then() to re-export as the module default
const MyPayslipsPage = lazyWithRetry(() => import("./components/hrms/PayrollDashboard").then(m => ({ default: m.MyPayslipsPage })));
const MyDocumentsPage = lazyWithRetry(() => import("./components/hrms/DocumentsVault").then(m => ({ default: m.MyDocumentsPage })));
const MyReimbursements = lazyWithRetry(() => import("./components/hrms/MyReimbursements"));
const MySwipeRequests = lazyWithRetry(() => import("./components/hrms/MySwipeRequests"));
const MyODRequests = lazyWithRetry(() => import("./components/hrms/MyODRequests"));
const MyLoans = lazyWithRetry(() => import("./components/hrms/MyLoans"));
const EmployeePortalLeaveApprovals = lazyWithRetry(() => import("./components/hrms/EmployeePortalLeaveApprovals"));
const EmployeeNotificationsPage = lazyWithRetry(() => import("./components/EmployeeNotificationsPage"));

// Self-Service Tenant Registration
const TenantSignup = lazyWithRetry(() => import("./components/TenantSignup"));
const SignupPending = lazyWithRetry(() => import("./components/SignupPending"));
const SignupPlanSelection = lazyWithRetry(() => import("./components/SignupPlanSelection"));
const PostApprovalOnboarding = lazyWithRetry(() => import("./components/PostApprovalOnboarding"));

// Detects known transient Floating UI / Radix UI positioning races that occur when a
// dropdown/popover/select unmounts mid-update in production (no StrictMode). These are
// null-reference races in @floating-ui autoUpdate — safe to retry by remounting.
// Also covers i18next / @radix-ui TypeScript helper collisions that can occur on
// first mount before minification is fixed (minifyIdentifiers was disabled in
// craco.config.js to prevent identifier name collisions after scope hoisting).
const isTransientFloatingUiError = (error) => {
  const msg = error?.message || '';
  return (
    msg.includes('Cannot convert undefined or null to object') ||
    msg.includes('getOwnPropertySymbols') ||
    msg.includes('whileElementsMounted') ||
    msg.includes("Cannot use 'in' operator") ||
    msg.includes('Property description must be an object') ||
    msg.includes('indexOf is not a function')
  );
};

// Browsers emit this benign warning when a ResizeObserver callback doesn't finish
// processing entries within one animation frame (e.g. a Radix Popover with a very
// tall/long scrollable list resizing as it opens). It never indicates broken
// functionality, but Chrome/CRA's dev overlay still surfaces it as an "Uncaught
// runtime error", which looks alarming and blocks the UI behind a red screen.
const isBenignResizeObserverLoopError = (message) => {
  const msg = message || '';
  return msg.includes('ResizeObserver loop completed with undelivered notifications') ||
    msg.includes('ResizeObserver loop limit exceeded');
};

// React Error Boundaries only catch errors during render / lifecycle methods.
// Errors thrown inside useEffect *cleanup* functions (e.g. Radix UI portal teardown
// when a popover is open during unmount) are NOT caught by error boundaries — they
// surface as uncaught JS errors and can crash the app. We suppress the known transient
// Floating UI / Radix UI cleanup crash here at the window level.
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (isTransientFloatingUiError(event.error)) {
      console.warn(
        '[Vivyawatch] Suppressed Radix UI / Floating UI cleanup error (safe to ignore):',
        event.error?.message
      );
      event.preventDefault(); // prevent browser "Uncaught Error" from re-throwing into React
      return;
    }
    if (isBenignResizeObserverLoopError(event.message || event.error?.message)) {
      console.warn('[Vivyawatch] Suppressed benign ResizeObserver loop warning (safe to ignore)');
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  });
}

// Error Boundary Component to catch and display runtime errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, errorKey: 0, recovering: false };
    // Timestamps of recent auto-recovery attempts. Used to detect a tight crash loop
    // (a genuinely broken subtree) vs. occasional transient races over a long session.
    this.recentRecoveries = [];
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error('React Error Boundary caught an error:', error, errorInfo);

    // Auto-recover from known transient Floating UI / Radix UI positioning races
    // and the "Cannot convert undefined or null to object" render-phase crash that
    // can occur on first load of a lazy chunk (e.g. /signin) in production when
    // a global component (ConversationalChat, LeadCaptureSystem) still has stale
    // state from the previous route for one render cycle.
    if (isTransientFloatingUiError(error)) {
      const now = Date.now();
      // Keep only attempts from the last 10 s.  Allow up to 5 retries so that
      // the first-navigation flash (stale shouldShow state) can settle even on
      // slow connections before we give up and show the error screen.
      this.recentRecoveries = this.recentRecoveries.filter((t) => now - t < 10000);
      if (this.recentRecoveries.length < 5) {
        this.recentRecoveries.push(now);
        this.setState({ recovering: true });
        // Increase delay slightly with each attempt so the event-loop has more
        // time to process the route-change effects before the next render.
        const delayMs = 300 + (this.recentRecoveries.length - 1) * 200;
        setTimeout(() => {
          this.setState((s) => ({ hasError: false, error: null, errorInfo: null, recovering: false, errorKey: s.errorKey + 1 }));
        }, delayMs);
      }
    }
  }

  render() {
    if (this.state.hasError) {
      // While auto-recovering from a transient Floating UI/Radix race, avoid flashing
      // the full-page red error screen — render nothing briefly until the remount.
      if (this.state.recovering) {
        return null;
      }
      return (
        <div style={{ padding: '40px', fontFamily: 'system-ui, sans-serif', backgroundColor: '#fff1f0', minHeight: '100vh' }}>
          <h1 style={{ color: '#cf1322', fontSize: '24px', marginBottom: '16px' }}>
             Something went wrong
          </h1>
          <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #ffccc7' }}>
            <h2 style={{ color: '#595959', fontSize: '16px', marginBottom: '12px' }}>Error Details:</h2>
            <pre style={{
              backgroundColor: '#f5f5f5',
              padding: '16px',
              borderRadius: '4px',
              overflow: 'auto',
              fontSize: '13px',
              color: '#cf1322'
            }}>
              {this.state.error && this.state.error.toString()}
            </pre>
            <h3 style={{ color: '#595959', fontSize: '14px', marginTop: '16px', marginBottom: '8px' }}>Component Stack:</h3>
            <pre style={{
              backgroundColor: '#f5f5f5',
              padding: '16px',
              borderRadius: '4px',
              overflow: 'auto',
              fontSize: '12px',
              color: '#595959',
              maxHeight: '300px'
            }}>
              {this.state.errorInfo && this.state.errorInfo.componentStack}
            </pre>
            <button
              onClick={() => {
                // Preserve the user's scroll position across the remount that
                // follows a "Try Again" click — without this, ScrollToTop's
                // mount effect treats the fresh app tree as a brand new page
                // load and jumps back to the top of the Home page.
                try {
                  sessionStorage.setItem('__vw_error_recovery_scroll', String(window.scrollY));
                } catch (e) { /* sessionStorage unavailable — ignore */ }
                this.setState((s) => ({ hasError: false, error: null, errorInfo: null, errorKey: s.errorKey + 1 }));
              }}
              style={{
                marginTop: '20px',
                marginRight: '12px',
                padding: '10px 20px',
                backgroundColor: '#1890ff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: '20px',
                padding: '10px 20px',
                backgroundColor: '#595959',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return <React.Fragment key={this.state.errorKey}>{this.props.children}</React.Fragment>;
  }
}


// ── Page loader — shown by Suspense while a lazy route chunk is fetching ────
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
    <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" aria-label="Loading…" />
  </div>
);

// Analytics wrapper component
const AnalyticsProvider = ({ children }) => {
  useAnalyticsInit();
  usePageTracking();
  useOutboundLinkTracking();
  // Platform-wide Activity Audit: logs "what did this user look at" for every
  // authenticated role. No-ops while logged out (marketing/public pages).
  usePlatformActivityPageView();
  return <>{children}</>;
};

// ── Impersonation Banner ────────────────────────────────────────────────────
// Persistent top bar shown whenever a Super Admin is impersonating a user.
// Must be rendered inside <BrowserRouter> so that useNavigate works.
const ImpersonationBanner = () => {
  const { currentUser, exitImpersonation, timeoutExitImpersonation } = useAuth();
  const [exiting, setExiting] = React.useState(false);
  const [secondsLeft, setSecondsLeft] = React.useState(null);
  // Guard: ensure the auto-exit fires exactly once even though the interval
  // keeps setting secondsLeft to 0 every second after expiry.
  const autoExitFiredRef = React.useRef(false);

  // TTL countdown — derive from currentUser.impersonation_expires_at if present
  React.useEffect(() => {
    if (!currentUser?.is_impersonating) return;
    const expiresAt = currentUser?.impersonation_expires_at;
    if (!expiresAt) return;
    const tick = () => {
      const diff = Math.max(0, Math.floor((new Date(expiresAt) - Date.now()) / 1000));
      setSecondsLeft(diff);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [currentUser]);

  // Auto-exit when the countdown reaches zero.
  // Uses the dedicated /impersonation-timeout-exit endpoint which requires only
  // the sa_actor_id cookie (still alive for 5 min after auth_token expires).
  React.useEffect(() => {
    if (secondsLeft !== 0 || autoExitFiredRef.current || exiting) return;
    autoExitFiredRef.current = true;
    setExiting(true);
    timeoutExitImpersonation()
      .catch(err => console.warn('[ImpersonationBanner] Auto-exit failed:', err))
      .finally(() => {
        // Always redirect to SA dashboard regardless of whether the API call
        // succeeded — the 401 interceptor in api.js is the backstop.
        window.location.href = '/super-admin/dashboard';
      });
  }, [secondsLeft, exiting, timeoutExitImpersonation]);

  if (!currentUser?.is_impersonating) return null;

  const actor = currentUser?.impersonation_actor_name || currentUser?.impersonation_actor || 'Super Admin';
  const targetName = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim();
  const targetEmail = currentUser?.email || '';

  const fmt = (s) => {
    if (s === null || s === undefined) return '';
    const m = Math.floor(s / 60), sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  const handleExit = async () => {
    setExiting(true);
    try {
      await exitImpersonation();
      // Hard redirect: the super-admin portal runs under a separate BrowserRouter
      // (basename="/super-admin") that is completely distinct from this admin portal router.
      window.location.href = '/super-admin/dashboard';
    } catch {
      setExiting(false);
    }
  };

  // Determine countdown display state
  const isExpired = secondsLeft === 0;
  const isWarning = secondsLeft !== null && secondsLeft > 0 && secondsLeft < 120;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-between gap-3 px-4 py-2 bg-purple-700 text-white shadow-lg"
      style={{ minHeight: '44px' }}
    >
      {/* Left: identity info */}
      <div className="flex items-center gap-2 text-sm min-w-0">
        <span className="text-purple-200 text-xs font-semibold uppercase tracking-wide flex-shrink-0">
           Impersonating
        </span>
        <span className="font-semibold truncate">{targetName}</span>
        <span className="text-purple-300 text-xs truncate hidden sm:inline">({targetEmail})</span>
        <span className="text-purple-300 text-xs hidden md:inline">· As: {actor}</span>
      </div>

      {/* Center: TTL countdown / expired notice */}
      {secondsLeft !== null && (
        isExpired ? (
          <div className="flex-shrink-0 text-xs font-mono px-2 py-0.5 rounded bg-red-600 text-white animate-pulse">
             Session expired — returning to dashboard…
          </div>
        ) : (
          <div className={`flex-shrink-0 text-xs font-mono px-2 py-0.5 rounded ${isWarning ? 'bg-red-500 text-white animate-pulse' : 'bg-purple-800 text-purple-200'}`}>
             {fmt(secondsLeft)} remaining
          </div>
        )
      )}

      {/* Right: manual exit button (hidden once expired / auto-exiting) */}
      {!isExpired && (
        <button
          onClick={handleExit}
          disabled={exiting}
          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-white text-purple-700 rounded-md text-xs font-semibold hover:bg-purple-50 disabled:opacity-60 transition-colors"
          aria-label="Exit impersonation and return to Super Admin dashboard"
        >
          {exiting ? 'Exiting…' : ' Exit Impersonation'}
        </button>
      )}
    </div>
  );
};

// ── Global RBAC Access-Denied Toast ─────────────────────────────────────────
// Listens for `vivya:access-denied` events dispatched by the api.js interceptor
// whenever a 403 RBAC permission error is received, and shows a toast alert.
const AccessDeniedToast = () => {
  React.useEffect(() => {
    const handleAccessDenied = (event) => {
      const message = event.detail?.message || 'You do not have permission to perform this action.';
      toast({
        title: 'Access Denied',
        description: message,
        variant: 'destructive',
        duration: 6000,
      });
    };
    window.addEventListener('vivya:access-denied', handleAccessDenied);
    return () => window.removeEventListener('vivya:access-denied', handleAccessDenied);
  }, []);
  return null;
};

// ── Bare /dashboard redirect ────────────────────────────────────────────────
// Converts legacy `/dashboard?tab=<tab>&...` links (emails, notifications) to
// `/dashboard/<tab>?...` so the tab and remaining query params are preserved.
const DashboardRedirect = () => {
  const { search, hash } = useLocation();
  const params = new URLSearchParams(search);
  const tab = params.get('tab') || 'overview';
  params.delete('tab');
  const qs = params.toString();
  return <Navigate to={`/dashboard/${encodeURIComponent(tab)}${qs ? `?${qs}` : ''}${hash}`} replace />;
};

function App() {
  return (
    <ErrorBoundary>
    <div className="App">
      <ThemeProvider>
        <CurrencyProvider>
          <AuthProvider>
            <SettingsProvider>
              <AlertProvider>
                <BrowserRouter>
                  <AnalyticsProvider>
                    <ScrollToTop />
                    {/* Compliance: persistent impersonation banner — visible on all pages */}
                    <ImpersonationBanner />
                    {/* Lead Capture - Exit Intent & Floating CTA */}
                    <LeadCaptureSystem />
                    {/* Conversational AI Chat - Value-first lead capture */}
                    <ConversationalChat />
                    <Suspense fallback={<PageLoader />}>
                    <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/solutions" element={<SolutionsPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/blog" element={<ResourcesPage />} />
            <Route path="/case-studies" element={<ResourcesPage />} />
            <Route path="/help" element={<ResourcesPage />} />

            {/* Educational Resources - AI-Rankable Knowledge Hub */}
            <Route path="/resources/privacy/employee-monitoring-without-violating-privacy" element={<EmployeeMonitoringPrivacyPage />} />
            <Route path="/resources/healthcare/hipaa-safe-employee-monitoring" element={<HIPAAMonitoringPage />} />
            <Route path="/resources/workforce-intelligence/privacy-first-workforce-intelligence" element={<WorkforceIntelligencePage />} />
            <Route path="/resources/privacy/gdpr-employee-monitoring-compliance" element={<GDPRMonitoringPage />} />
            <Route path="/resources/compliance/soc2-employee-monitoring-guide" element={<SOC2MonitoringPage />} />
            <Route path="/resources/workforce-intelligence/ai-monitoring-privacy" element={<AIMonitoringPrivacyPage />} />
            <Route path="/partner" element={<PartnerPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/compare" element={<CompareCompetitorsPage />} />
            <Route path="/tools" element={<ToolsPage />} />
            <Route path="/tryus" element={<ToolsPage />} />
            <Route path="/try" element={<ToolsPage />} />
            <Route path="/deployment-guide" element={<DeploymentGuidePage />} />

            {/* Feature Sub-Pages */}
            <Route path="/features/screenshot-monitoring" element={<ScreenshotMonitoringPage />} />
            <Route path="/features/productivity-analytics" element={<ProductivityAnalyticsPage />} />
            <Route path="/features/compliance-suite" element={<ComplianceSuitePage />} />
            <Route path="/features/insider-risk" element={<InsiderRiskPage />} />
            
            {/* Legal Pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/security-compliance" element={<SecurityCompliancePage />} />

            {/* Device Consent Page - PUBLIC (no login required, uses device JWT) */}
            <Route path="/device-consent" element={<DeviceConsentPage />} />

            <Route path="/signin" element={<SignInPage />} />

            {/* SSO Callback Route - PUBLIC (handles IdP redirect) */}
            <Route path="/sso/callback" element={<SSOCallback />} />

            {/* Self-Service Tenant Registration Routes */}
            <Route path="/signup" element={<TenantSignup />} />
            <Route path="/signup/pending" element={<SignupPending />} />
            <Route path="/signup/plan" element={<SignupPlanSelection />} />
            <Route path="/onboarding/setup" element={<PostApprovalOnboarding />} />

            {/* Demo Route - Tab Highlighting */}
            <Route path="/demo/tab-highlight" element={<TabHighlightDemo />} />
            
            {/* First-time login and profile routes */}
            <Route 
              path="/password-change" 
              element={
                <ProtectedRoute>
                  <PasswordChange />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/payment" 
              element={
                <ProtectedRoute>
                  <PaymentPage />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <EmployeePortalLayout>
                    <ViewProfile />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/edit"
              element={
                <ProtectedRoute>
                  <EmployeePortalLayout>
                    <EditProfile />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/change-password"
              element={
                <ProtectedRoute>
                  <EmployeePortalLayout>
                    <PasswordChange />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/settings"
              element={
                <ProtectedRoute>
                  <EmployeePortalLayout>
                    <AccountSettings />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />

            {/* Employee Notifications — full page in portal layout */}
            <Route
              path="/notifications"
              element={
                <ProtectedRoute requiredRoles={["employee", "admin", "super_admin", "org_admin", "client_admin", "super_client_admin", "manager"]}>
                  <EmployeePortalLayout>
                    <EmployeeNotificationsPage />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />

            {/* Protected Routes */}
            {/* Bare /dashboard → redirect to default tab */}
            <Route path="/dashboard" element={<DashboardRedirect />} />
            <Route
              path="/dashboard/*"
              element={
                <ProtectedRoute requiredPermission="view_employees">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/setup" 
              element={
                <ProtectedRoute requiredRoles={["it_admin", "super_admin"]}>
                  <SetupLanding />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/advanced-monitoring-setup" 
              element={
                <ProtectedRoute requiredRoles={["it_admin", "super_admin"]}>
                  <AdvancedMonitoringSetup />
                </ProtectedRoute>
              } 
            />
            
            {/* Centralized Policy Management Route */}
            <Route
              path="/policy-management"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <PolicyManagementDashboard />
                </ProtectedRoute>
              }
            />

            {/* Alerts & Notifications Route — sub-tab in path (persists on refresh / shared links) */}
            <Route
              path="/alerts"
              element={<Navigate to="/alerts/alerts" replace />}
            />
            <Route
              path="/alerts/:tab"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin", "manager"]}>
                  <AlertsDashboard />
                </ProtectedRoute>
              }
            />

            {/* Team Productivity Configuration */}
            <Route
              path="/teams/:teamId/productivity"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin", "manager"]}>
                  <TeamProductivityConfigPage />
                </ProtectedRoute>
              }
            />

            {/* Billing & Metering Route */}
            <Route
              path="/billing"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <BillingDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* EPIC AX5 - PIMS Registry Route */}
            <Route 
              path="/pims-registry" 
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <PIMSRegistryDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* EPIC AX5 - DPIA Route */}
            <Route 
              path="/dpia" 
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <DPIADashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* EPIC AX5 - Consent Lifecycle Route */}
            <Route
              path="/consent-lifecycle"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin", "client_admin", "super_client_admin", "employee"]}>
                  <ConsentLifecycleDashboard />
                </ProtectedRoute>
              }
            />

            {/* EPIC AX5 - Privacy Training Route */}
            <Route
              path="/privacy-training"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin", "client_admin", "super_client_admin", "employee"]}>
                  <PrivacyTrainingDashboard />
                </ProtectedRoute>
              }
            />

            {/* Employee Portal Dashboard - wrapped in sidebar layout */}
            <Route
              path="/employee-portal"
              element={
                <ProtectedRoute requiredRoles={["employee", "manager", "admin", "super_admin", "org_admin", "client_admin", "super_client_admin"]}>
                  <EmployeePortalLayout>
                    <EmployeePortalDashboard noHeader />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />

            {/* Employee Leave Portal — sidebar layout, no separate back button needed */}
            <Route
              path="/my-leave"
              element={
                <ProtectedRoute requiredRoles={["employee", "manager", "admin", "super_admin", "org_admin", "client_admin", "super_client_admin"]}>
                  <EmployeePortalLayout>
                    <EmployeeLeavePortal />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />

            {/* Employee Payslips Portal — self-service payslip history */}
            <Route
              path="/my-payslips"
              element={
                <ProtectedRoute requiredRoles={["employee", "manager", "admin", "super_admin", "org_admin", "client_admin", "super_client_admin"]}>
                  <EmployeePortalLayout>
                    <MyPayslipsPage />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />

            {/* Employee Documents Portal — self-service document checklist */}
            <Route
              path="/my-documents"
              element={
                <ProtectedRoute requiredRoles={["employee", "manager", "admin", "super_admin", "org_admin", "client_admin", "super_client_admin"]}>
                  <EmployeePortalLayout>
                    <MyDocumentsPage />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />

            {/* Employee Reimbursements Portal — self-service expense claims */}
            <Route
              path="/my-reimbursements"
              element={
                <ProtectedRoute requiredRoles={["employee", "manager", "admin", "super_admin", "org_admin", "client_admin", "super_client_admin"]}>
                  <EmployeePortalLayout>
                    <MyReimbursements />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />

            {/* Employee Portal Leave Approvals — for employees configured as a
                leave approver (any level), even without admin/manager portal access */}
            <Route
              path="/my-leave-approvals"
              element={
                <ProtectedRoute requiredRoles={["employee", "manager", "admin", "super_admin", "org_admin", "client_admin", "super_client_admin"]}>
                  <EmployeePortalLayout>
                    <EmployeePortalLeaveApprovals />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />

            {/* Employee Swipe Corrections Portal */}
            <Route
              path="/my-swipe-requests"
              element={
                <ProtectedRoute requiredRoles={["employee", "manager", "admin", "super_admin", "org_admin", "client_admin", "super_client_admin"]}>
                  <EmployeePortalLayout>
                    <MySwipeRequests />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />

            {/* Employee On-Duty Requests Portal */}
            <Route
              path="/my-od-requests"
              element={
                <ProtectedRoute requiredRoles={["employee", "manager", "admin", "super_admin", "org_admin", "client_admin", "super_client_admin"]}>
                  <EmployeePortalLayout>
                    <MyODRequests />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />

            {/* Employee Loans Portal */}
            <Route
              path="/my-loans"
              element={
                <ProtectedRoute requiredRoles={["employee", "manager", "admin", "super_admin", "org_admin", "client_admin", "super_client_admin"]}>
                  <EmployeePortalLayout>
                    <MyLoans />
                  </EmployeePortalLayout>
                </ProtectedRoute>
              }
            />

            {/* Leave Calendar Route */}
            <Route
              path="/leave-calendar"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin", "client_admin", "super_client_admin"]}>
                  <LeaveCalendar />
                </ProtectedRoute>
              }
            />

            {/* Mobile-web Clock-in Route - geofenced + selfie capture */}
            <Route
              path="/clock-in"
              element={
                <ProtectedRoute requiredRoles={["employee", "admin", "super_admin", "org_admin", "client_admin", "super_client_admin", "manager"]}>
                  <MobileClockIn />
                </ProtectedRoute>
              }
            />

            {/* Shared-device Kiosk Route - operator stays logged in */}
            <Route
              path="/attendance-kiosk"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin", "client_admin", "super_client_admin", "manager"]}>
                  <AttendanceKiosk />
                </ProtectedRoute>
              }
            />

            {/* EPIC AX7 - Data Catalog Route */}
            <Route 
              path="/data-catalog" 
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <DataCatalogDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* EPIC AX7 - Retention Orchestrator Route */}
            <Route 
              path="/retention-orchestrator" 
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <RetentionOrchestratorDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* EPIC AX9 - Certification Tracker Route */}
            <Route 
              path="/certification-tracker" 
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <CertificationTrackerDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* EPIC AX9 - CAPA Management Route */}
            <Route 
              path="/capa" 
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <CAPADashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* EPIC AX9 - Auditor Portal Route */}
            <Route 
              path="/auditor-portal" 
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <AuditorPortalDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* EPIC AX10 - AI Compliance Advisor Route */}
            <Route 
              path="/ai-compliance-advisor" 
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <AIComplianceAdvisorDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* EPIC AE - Screen Intelligence Route */}
            <Route 
              path="/screen-intelligence" 
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <ScreenIntelligenceDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Application Monitoring Route — sub-tab in path */}
            <Route
              path="/application-monitoring"
              element={<Navigate to="/application-monitoring/overview" replace />}
            />
            <Route
              path="/application-monitoring/:tab"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <ApplicationMonitoringDashboardNew />
                </ProtectedRoute>
              }
            />
            
            {/* EPIC AP - AI ROI & Impact Estimator Route */}
            <Route 
              path="/roi-estimator" 
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <ROIEstimatorDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* EPIC AQ - Closed-Loop AI Governance Route */}
            <Route 
              path="/governance-loop" 
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <GovernanceLoopDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Advanced Analytics Route */}
            <Route
              path="/advanced-analytics"
              element={
                <ProtectedRoute requiredRoles={["super_admin"]}>
                  <AdvancedAnalyticsDashboard />
                </ProtectedRoute>
              }
            />

            {/* Organization/Tenant Management Route */}
            <Route 
              path="/organizations" 
              element={
                <ProtectedRoute requiredRoles={["super_admin"]}>
                  <TenantManagementDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Payment Provider Configuration Route */}
            <Route
              path="/payment-config"
              element={
                <ProtectedRoute requiredRoles={["super_admin"]}>
                  <PaymentConfigDashboard />
                </ProtectedRoute>
              }
            />

            {/* Forensics Approvals Route (Security Admin) */}
            <Route
              path="/forensics-approvals"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <ForensicsApprovalsDashboard />
                </ProtectedRoute>
              }
            />

            {/* NEW: Advanced Security & AI Routes (from vrmani branch) */}
            {/* Fake Activity lives inside the admin shell (sidebar + header) like every
                other module. The old shell-less top-level URLs are kept as redirects
                so existing bookmarks and deep links keep working. */}
            <Route
              path="/fake-activity"
              element={<Navigate to="/dashboard/fake-activity" replace />}
            />
            <Route
              path="/fake-activity/dashboard"
              element={<Navigate to="/dashboard/fake-activity/dashboard" replace />}
            />
            <Route
              path="/fake-activity/configuration"
              element={<Navigate to="/dashboard/fake-activity/configuration" replace />}
            />
            <Route
              path="/fake-activity/*"
              element={<Navigate to="/dashboard/fake-activity" replace />}
            />
            <Route
              path="/fake-activity-settings"
              element={<Navigate to="/dashboard/fake-activity/configuration" replace />}
            />
            <Route
              path="/ueba"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <UEBADashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/soar"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <SOARDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sentiment-analysis"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <SentimentAnalysisDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/email-dlp"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin", "client_admin", "super_client_admin"]}>
                  <EmailDLPDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/exfiltration-detection"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <ExfiltrationDetectionDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/github-activity"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <GitHubActivityDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/security-policies"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <SecurityPolicyDashboard />
                </ProtectedRoute>
              }
            />
            {/* Email Analytics - Email metadata analytics for workload visibility — sub-tab in path */}
            <Route
              path="/email-analytics"
              element={<Navigate to="/email-analytics/overview" replace />}
            />
            <Route
              path="/email-analytics/:tab"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <EmailAnalyticsDashboard />
                </ProtectedRoute>
              }
            />

            {/* Cloud Documents - M365/Google Workspace Document Context — sub-tab in path */}
            <Route
              path="/cloud-documents"
              element={<Navigate to="/cloud-documents/overview" replace />}
            />
            <Route
              path="/cloud-documents/:tab"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <CloudDocumentsDashboard />
                </ProtectedRoute>
              }
            />

            {/* Automation Rules Builder - Visual rule builder for incident workflows */}
            <Route
              path="/automation-rules"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <AutomationRulesBuilder />
                </ProtectedRoute>
              }
            />

            {/* Ticketing Integrations - Jira, ServiceNow, Freshservice */}
            <Route
              path="/ticketing-integrations"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <TicketingIntegrations />
                </ProtectedRoute>
              }
            />

            {/* M365 Integration Dashboards */}
            <Route
              path="/sharepoint"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <SharePointDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/yammer"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <YammerDashboard />
                </ProtectedRoute>
              }
            />
            {/* M365 Audit — sub-tab in path (persists on refresh / shared links) */}
            <Route
              path="/m365-audit"
              element={<Navigate to="/m365-audit/overview" replace />}
            />
            <Route
              path="/m365-audit/:tab"
              element={
                <ProtectedRoute requiredRoles={["admin", "super_admin", "org_admin"]}>
                  <M365AuditDashboard />
                </ProtectedRoute>
              }
            />

            {/* Super Admin Routes */}
            <Route path="/super-admin" element={<SuperAdminLogin />} />
            <Route path="/super-admin/login" element={<SuperAdminLogin />} />
            <Route path="/super-admin-login" element={<SuperAdminLogin />} />
            {/* Bare /super-admin/dashboard → redirect to default tab */}
            <Route path="/super-admin/dashboard" element={<Navigate to="/super-admin/dashboard/dashboard" replace />} />
            <Route
              path="/super-admin/dashboard/*"
              element={
                <ProtectedRoute requiredRole="super_admin">
                  <SuperAdminDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Placeholder Routes */}
            <Route 
              path="/features" 
              element={
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center space-y-4">
                    <h2 className="text-2xl font-bold">Features Page</h2>
                    <p className="text-gray-600 dark:text-gray-200">Coming Soon - Full feature showcase</p>
                  </div>
                </div>
              } 
            />
            <Route 
              path="/pricing" 
              element={
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center space-y-4">
                    <h2 className="text-2xl font-bold">Pricing Page</h2>
                    <p className="text-gray-600 dark:text-gray-200">Coming Soon - Detailed pricing plans</p>
                  </div>
                </div>
              } 
            />
            
            {/* 404 Route */}
            <Route 
              path="*" 
              element={
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center space-y-4">
                    <h2 className="text-4xl font-bold text-gray-900">404</h2>
                    <p className="text-gray-600 dark:text-gray-200">Page not found</p>
                    <a 
                      href="/" 
                      className="inline-block px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Go Home
                    </a>
                  </div>
                </div>
              }
            />
                    </Routes>
                    </Suspense>
                    <Toaster />
                    <LicenseLockout />
                    <AccessDeniedToast />
                  </AnalyticsProvider>
                </BrowserRouter>
              </AlertProvider>
            </SettingsProvider>
          </AuthProvider>
        </CurrencyProvider>
      </ThemeProvider>
    </div>
    </ErrorBoundary>
  );
}

export default App;
